import React, { useContext } from 'react';
import classnames from 'classnames';

import { Grid, Container, Button, Typography, Card, CardActions, CardContent } from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

import GenerationContext from '@hydra/common/components/GenerationContext';
import CreationToolbar from '@hydra/routes/create/components/CreationToolbar';
import CreateHeader from '@hydra/routes/create/components/CreateHeader';
import { Translate, TrialLimitDialog } from '@hydra/common/components';
import GenerationForm from '@hydra/common/components/GenerationForm';
import { UnresolvedDetails } from '@hydra/common/error';
import { useUserContext } from '@hydra/common/context';

import { isValidReviewUrl, getHostname } from '@hydra/utils/urlUtil';
import redirectToUrl from '@hydra/utils/redirectToUrl';
import { getProject } from '@hydra/utils/projectApi';
import { createReview } from '@hydra/utils/reviewApi';
import { sendEvent } from '@hydra/utils/gtm';
import { isLite } from '@hydra/utils/userUtil';

import { useRecaptchaV3, useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  helpText: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.fontSizeSmall
  },
  title: {
    marginTop: theme.spacing(9),
    marginBottom: theme.spacing(2),
    fontWeight: theme.typography.fontWeightSemibold
  },
  card: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(4)
  },
  cardActions: {
    paddingTop: theme.spacing(2),
    justifyContent: 'center'
  },
  cardButton: {
    '&:hover, &:active, &:focus': {
      color: theme.palette.primary.contrastText
    }
  }
}));

interface IHandleReviewSubmit {
  dispatch: (payload: { type: string, payload?: string }) => void,
  url: string,
  locale: string
}

const handleReviewSubmit = async ({ dispatch, url, locale }: IHandleReviewSubmit) => {
  if (url) {
    dispatch({ type: 'ON_SUBMIT' });
    if (!isValidReviewUrl(url)) {
      return dispatch({ type: 'ON_ERROR_INVALID_URL' });
    }

    if (!isValidReviewUrl(url, { homepageOnly: true })) {
      const project = await getProject(getHostname(url));
      if (!project) {
        return dispatch({ type: 'ON_ERROR_NO_INTERNAL_PAGE_NO_HOMEPAGE_REVIEW' });
      }
    }

    try {
      const { review, generation } = await createReview(url);
      if (generation) {
        redirectToUrl({ url: `/${locale}/review/${encodeURIComponent(review.url)}?${new URLSearchParams({ generationId: generation.id })}` });
      }
    } catch (err: any) {
      const { errorCode, ...payload } = err;
      if (errorCode === 'EXISTS') {
        redirectToUrl({ url: `/${locale}/review/${encodeURIComponent(url)}` });
      } else if (errorCode === 'INVALID_URL') {
        dispatch({ type: 'ON_ERROR_INVALID_URL' });
      } else if (errorCode === 'UNRESOLVABLE_URL') {
        dispatch({ type: 'ON_ERROR_UNRESOLVABLE_URL', payload });
      } else if (errorCode === 'TRIAL_REVIEW_LIMIT') {
        dispatch({ type: 'ON_ERROR_TRIAL_REVIEW_LIMIT' });
      } else {
        dispatch({ type: 'ON_ERROR' });
        throw err;
      }
    }
  }
};

interface CreateReviewProps {
  data: {
    reviewCount: number
  }
}

export default function CreateReview ({ data }: CreateReviewProps) {
  const { reviewCount } = data;
  const [{ url, isError, error, errorMessage }, dispatch] = useContext<any>(GenerationContext);
  const { translate, locale } = useI18n();
  const classes = useStyles();
  const { user } = useUserContext();
  const maxProjects = user?.maxProjects || 0;

  const isLiteUser = isLite(user);
  let maxReviews: null | number = null;
  if (user && isLiteUser && typeof user.maxReports === 'number') {
    maxReviews = !(user.maxReports === 0 || user.maxReports === -1) ? user.maxReports : null;
  }
  const reviewsLeft = (isLiteUser && maxReviews !== null) ? maxReviews - reviewCount : null;

  const { verifyCaptcha } = useRecaptchaV3('create-review');

  const handleSubmit = async () => {
    await verifyCaptcha();
    sendEvent('cflow-review-create-button');
    handleReviewSubmit({ dispatch, url, locale });
  };

  const CreateReviewContent = !isLiteUser || (reviewsLeft !== null && reviewsLeft > 0)
    ? (
      <>
        <Typography variant='h3' component='h1' className={classes.title} gutterBottom>
          <Translate content='cflow_create_review_title' />
        </Typography>
        <GenerationForm
          className='nr-review-generation-bar'
          placeholder={translate('generation_form_review_placeholder')}
          buttonText={translate('cflow_review_cta_submit')}
          handleSubmit={handleSubmit}
        />
        {
          isError && error && errorMessage === 'unresolvable' && <UnresolvedDetails error={error} />
        }
      </>
      )
    : (
      <Card className={classes.card} elevation={0}>
        <CardContent>
          <Typography align='center' variant='h2' gutterBottom>
            <Translate content='cflow_upgrade_reviews_plan_title' />
          </Typography>
          <Typography align='center' variant='body1' paragraph>
            <Translate content='cflow_upgrade_plan_details' />
          </Typography>
        </CardContent>
        <CardActions className={classes.cardActions}>
          <Button
            className={classes.cardButton}
            href={`/${locale}/user/plan`}
            size='large'
            variant='contained'
            color='primary'
          >
            <Translate content='cflow_upgrade_plan_cta' />
          </Button>
        </CardActions>
      </Card>
      );

  return (
    <>
      <TrialLimitDialog
        open={isError && errorMessage === 'tiral_review_limit'}
        onClose={() => dispatch({ type: 'ON_RESET' })}
      />
      <Container maxWidth='md'>
        <CreateHeader href={`/${locale}/audit`} />
      </Container>
      <Container
        fixed
        className={classnames(
          classes.root,
          'nr-cflow-create-review-page'
        )}
      >
        <Grid container justify='center'>
          <Grid item xs={12} sm={10} md={8}>
            <>
              {(maxProjects > 0 || isLiteUser) && <CreationToolbar active='review' />}
              {CreateReviewContent}
            </>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
